import { commands, isLiveRuntime, commandPayload } from './runtime'

type Popup = { resumeTransaction: (code: string, callbacks: {
  onSuccess: () => void; onCancel: () => void; onError: () => void
}) => void }
type SDK = new () => Popup
declare global { interface Window { PaystackPop?: SDK; Paystack?: SDK } }
let sdkLoad: Promise<SDK> | undefined

/** The one label every pay button uses, so the cart and Orders read alike. */
export const formatPayLabel = (amountMinor: number): string =>
  `Pay ₦${(amountMinor / 100).toLocaleString('en-NG', { maximumFractionDigits: 2 })}`

export function loadPaystack(): Promise<SDK> {
  const loaded = window.PaystackPop ?? window.Paystack
  if (loaded) return Promise.resolve(loaded)
  if (sdkLoad) return sdkLoad
  sdkLoad = new Promise<SDK>((resolve, reject) => {
    const script = document.createElement('script')
    const finish = (error?: Error) => {
      clearTimeout(timer)
      script.onload = null
      script.onerror = null
      const sdk = window.PaystackPop ?? window.Paystack
      if (error || !sdk) { script.remove(); reject(error ?? new Error('Paystack is unavailable.')) }
      else resolve(sdk)
    }
    const timer = window.setTimeout(() => finish(new Error('Paystack took too long to load. Try again.')), 15000)
    script.src = 'https://js.paystack.co/v2/inline.js'
    script.async = true
    script.onload = () => finish()
    script.onerror = () => finish(new Error('Paystack could not load. Check your connection and try again.'))
    document.head.appendChild(script)
  }).catch((error: unknown) => { sdkLoad = undefined; throw error })
  return sdkLoad
}

export async function openPaystack(accessCode: string): Promise<void> {
  if (!accessCode) throw new Error('Payment checkout is incomplete. Try again from Orders.')
  const SDK = await loadPaystack()
  return new Promise<void>((resolve, reject) => {
    let settled = false
    const finish = (error?: Error) => {
      if (settled) return
      settled = true
      clearTimeout(timer)
      if (error) reject(error)
      else resolve()
    }
    const timer = window.setTimeout(() => finish(new Error('Payment confirmation timed out. Check Orders before paying again.')), 10 * 60 * 1000)
    try {
      new SDK().resumeTransaction(accessCode, {
        onSuccess: () => finish(),
        onCancel: () => finish(new Error('Checkout closed. Your order remains available in Orders.')),
        onError: () => finish(new Error('Paystack could not complete checkout. Check Orders before trying again.')),
      })
    } catch { finish(new Error('Paystack checkout could not open. Try again from Orders.')) }
  })
}

/**
 * Pay for one order, or for a whole basket of them with a single payment.
 *
 * An order spanning several kitchens is passed as `orderIds`. The server prices
 * the sum of their unpaid balances and charges it once, which is why a customer
 * ordering from three kitchens sees one payment page rather than three.
 *
 * `amountMinor` is no longer sent. The server prices every order and an order is
 * paid in full or not at all, so a client-supplied amount could only ever be
 * wrong.
 */
export async function payForOrders(
  orderIds: readonly string[],
  profileId: string,
): Promise<void> {
  if (!isLiveRuntime()) throw new Error('Connect to ChooseChow to make a payment.')
  const ids = orderIds.map((id) => id.trim()).filter((id) => id.length > 0)
  const unique = [...new Set(ids)]
  if (unique.length === 0) throw new Error('There is nothing to pay for.')
  const session = commandPayload(await commands.billing.createCheckoutSession({
    // A single order keeps the single-order field, so a one-kitchen basket
    // takes exactly the path it always did.
    ...(unique.length === 1
      ? { order_id: unique[0] }
      : { order_ids: unique }),
    payer_profile_id: profileId,
    provider: 'paystack',
    currency: 'NGN',
  }))
  await completeCheckout(session)
}

/** Pay for one order. Kept for callers that genuinely have a single order. */
export async function payForOrder(
  orderId: string,
  profileId: string,
  _amountMinor?: number,
): Promise<void> {
  await payForOrders([orderId], profileId)
}

export async function payForSubscription(planCode: string, profileId: string): Promise<void> {
  if (!isLiveRuntime()) throw new Error('Connect to ChooseChow to purchase a plan.')
  const subscription = commandPayload(await commands.billing.createSubscription({ profile_id: profileId, plan_code: planCode, billing_cycle: 'monthly' }))
  if (!subscription.id) throw new Error('Subscription could not be prepared.')
  const session = commandPayload(await commands.billing.createCheckoutSession({ subscription_id: subscription.id, payer_profile_id: profileId, provider: 'paystack' }))
  await completeCheckout(session)
}

export class PaymentVerificationPendingError extends Error {
  constructor(message: string, readonly reference: string) {
    super(message)
    this.name = 'PaymentVerificationPendingError'
  }
}

async function completeCheckout(session: Record<string, unknown>): Promise<void> {
  const reference = String(session.reference ?? '')
  if (!reference) throw new Error('Payment reference is missing. Try again from Orders.')
  let popupError: unknown
  try { await openPaystack(String(session.access_code ?? '')) } catch (error) { popupError = error }
  // Closing the provider window can race its success callback. Always verify.
  try {
    const payment = commandPayload(await commands.billing.confirmPayment({ provider: 'paystack', reference }))
    if (payment.status !== 'captured') throw new Error('Payment is awaiting verification. Check Orders before paying again.')
  } catch (error) {
    const reason = popupError ?? error
    throw new PaymentVerificationPendingError(
      `${reason instanceof Error ? reason.message : 'Payment could not be confirmed.'} Check Orders before paying again.`, reference,
    )
  }
}
